import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Hook for the Theme Strings browse page (Phase 14, plan 14-04).
 *
 * KEY DELTA vs Phase 13's useMetaobjectDefinitions: filters are SERVER-SIDE
 * — every filter change triggers a refetch with query params, NOT a client-side
 * useMemo. This is required because the backend serves ~3,500 rows in a single
 * response and we want pagination + filtering to flow through the same SQL
 * that powers the per-locale status pivot.
 */
export default function useThemeStrings() {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (server-side per D-24)
  const [resourceType, setResourceType] = useState(''); // '' = all
  const [keyPrefix, setKeyPrefix] = useState('');       // '' = all
  const [policy, setPolicy] = useState('all');          // 'translate' | 'skip' | 'auto-skipped' | 'all'
  const [status, setStatus] = useState('all');          // 'all' | 'missing'
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms — matches Phase 13)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (resourceType) params.resource_type = resourceType;
      if (keyPrefix) params.key_prefix = keyPrefix;
      if (policy && policy !== 'all') params.policy = policy;
      if (status && status !== 'all') params.status = status;
      if (debouncedQ) params.q = debouncedQ;

      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/theme-strings/', { params }),
        axios.get('/api/transly/theme-strings/sync-status/'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load theme strings');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, resourceType, keyPrefix, policy, status, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters change (avoid stranded pages)
  useEffect(() => {
    setPage(1);
  }, [resourceType, keyPrefix, policy, status, debouncedQ]);

  const updatePolicy = useCallback(async (themeStringId, newPolicy) => {
    // Optimistic update — flip the row first, then PATCH; rollback on error.
    const prev = rows;
    setRows((curr) =>
      curr.map((r) =>
        r.id === themeStringId
          ? {
              ...r,
              translation_policy: newPolicy,
              auto_skip_reason: newPolicy === null ? r.auto_skip_reason : null,
            }
          : r,
      ),
    );
    try {
      await axios.patch(`/api/transly/theme-strings/${themeStringId}/policy/`, {
        policy: newPolicy,
      });
      // For policy=null (reset to auto), the server re-classified — refetch this row's truth.
      if (newPolicy === null) {
        await fetchData();
      }
    } catch (err) {
      // Rollback
      setRows(prev);
      throw err;
    }
  }, [rows, fetchData]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/theme-strings/sync/');
  }, []);

  return {
    // Data
    rows,
    total,
    loading,
    error,
    // Filters
    resourceType,
    setResourceType,
    keyPrefix,
    setKeyPrefix,
    policy,
    setPolicy,
    status,
    setStatus,
    q,
    setQ,
    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize,
    // Sync
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    triggerSync,
    // Actions
    updatePolicy,
    fetchData,
  };
}
